import Vue from 'vue';
import Axios from 'axios';

// 1. The Single Source of Truth
const sessionState = {
    session: {
        token: null,
        expired: false,
    },
    install() {
        Object.defineProperty(Vue.prototype, '$global', {
            get() { return sessionState; }
        });
    }
};

// --- Private Helper Functions ---

// Every deploy bumps GeneralSettings.frontend_version (via `php artisan
// frontend:bump-version`) -- comparing it against this build's own baked-in
// VUE_APP_BUILD_VERSION (see vue.config.js) catches browser tabs still
// running an older bundle and reloads them, rather than leaving them stuck
// on stale JS/CSS (filenameHashing is off, so asset URLs never change on
// their own to force a refetch).
const FRONTEND_VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
let frontendVersionCheckIntervalId = null;

function checkFrontendVersion(config) {
    const serverVersion = config && config.frontend_version;
    const currentVersion = process.env.VUE_APP_BUILD_VERSION;

    if (!serverVersion || !currentVersion || String(serverVersion) === String(currentVersion)) {
        return;
    }

    // Guard against reload loops: if the deployed bundle's baked-in version
    // never ends up matching the server's frontend_version (e.g. the two
    // were bumped out of sync during a deploy), every page load would
    // otherwise detect the same mismatch and reload again forever. Reload
    // at most once per mismatched server version instead.
    try {
        const reloadedForVersion = sessionStorage.getItem('kgkReloadedForVersion');
        if (reloadedForVersion === String(serverVersion)) {
            console.warn('Frontend version still mismatched after reload -- not reloading again to avoid a loop.', { serverVersion, currentVersion });
            return;
        }
        sessionStorage.setItem('kgkReloadedForVersion', String(serverVersion));
    } catch (e) {
        // sessionStorage inaccessible (e.g. private browsing) -- fall through
        // and reload anyway, same as before this guard existed.
    }

    console.warn('New frontend version deployed, reloading...', { serverVersion, currentVersion });
    window.location.reload();
}

async function pollFrontendVersion() {
    try {
        const response = await Axios.get(window.baseroute + '/wp-json/kgk-db/v1/configuration/config');
        checkFrontendVersion(response.data);
    } catch (e) {
        console.error('Could not check frontend version', e);
    }
}

async function fetchMenu(){
    const response = await Axios.get(window.baseroute + '/wp-json/kgk-db/v1/configuration/getMenu');
    window.kgkMenu = response.data;
    return window.kgkMenu;
}

async function getRoutes(){
    const response = await Axios.get(window.baseroute + '/wp-json/kgk-db/v1/configuration/routes');
    const routeHelper = response.data;
    Object.keys(routeHelper).forEach(key => {
        if(String(routeHelper[key]).includes('wp-json')){
            routeHelper[key] = window.baseroute + routeHelper[key]; // Prepend the string to each value
        }else{
            Object.keys(routeHelper[key]).forEach(key2 => {
                if(String(routeHelper[key][key2]).includes('wp-json')){
                    routeHelper[key][key2] = window.baseroute + routeHelper[key][key2]; // Prepend the string to each value
                }else{
                    Object.keys(routeHelper[key][key2]).forEach(key3 => {
                        routeHelper[key][key2][key3] = window.baseroute + routeHelper[key][key2][key3];
                    })
                }
            });
        }
        });

    window.kgkTexts = {};

    window.kgkRoutes = routeHelper;
    window.kgkRoutes.updated = true;

    const kgkConfig = await Axios.get(window.baseroute + '/wp-json/kgk-db/v1/configuration/config')
    window.kgkConfig = kgkConfig.data;
    checkFrontendVersion(window.kgkConfig);
    const kgkSponsors = await Axios.get(window.baseroute + '/wp-json/kgk-db/v1/configuration/sponsors')
    window.kgkSponsors = kgkSponsors.data;
    window.kgkConfig.updated = true;
    await fetchMenu();
    window.kgkConfig.updated = true;
    const kgkTexts = await Axios.get(window.baseroute + '/wp-json/kgk-db/v1/configuration/getTexts')
    window.kgkTexts = kgkTexts.data.texts;
    window.kgkConfig.updated = true;
}

function getCookieValue(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? match[2] : '';
}

function setTokenStorages(token) {
    document.cookie = `kgkDbSessionToken=${token}; max-age=${60*60*24*365}; path=/`;
    localStorage.setItem('kgkDbSessionToken', token);
}

function resetTokenStorages() {
    document.cookie = 'kgkDbSessionToken=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    localStorage.removeItem('kgkDbSessionToken');
}

function updateUI(isLoggedIn) {
    if (isLoggedIn) {
        document.body.classList.add('kgk-logged-in');
    } else {
        document.body.classList.remove('kgk-logged-in');
    }
}

// --- The Core Logic ---

function loadTokenFromStorage() {
    // Check LocalStorage first, then Cookie
    let token = localStorage.getItem('kgkDbSessionToken') || getCookieValue('kgkDbSessionToken');

    if (token && token.length > 10) { // Reduced length check for safety
        sessionState.token = token;
        updateUI(true);
        return token;
    }

    sessionState.token = null;
    updateUI(false);
    return null;
}

Axios.interceptors.request.use(
    (config) => {
        // Use the token from your sessionState
        const token = sessionState.token;

        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Optional: Handle 401 errors (token expired)
Axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            console.warn("Token expired or invalid. Logging out...");
            // You can call your logout function here
            // session-handling module needs to be imported to call logout()
        }
        return Promise.reject(error);
    }
);

// --- Exported Module ---

export default {
    init: async () => {
        
        // 1. Load existing session immediately
        loadTokenFromStorage();

        try {
            await getRoutes();
        } catch (e) {
            console.error("Failed to load Routes and Configs from server...", e);
        }

        // init() is called twice at startup (see main.js) -- guard against
        // registering the poll interval more than once.
        if (!frontendVersionCheckIntervalId) {
            frontendVersionCheckIntervalId = setInterval(pollFrontendVersion, FRONTEND_VERSION_CHECK_INTERVAL_MS);
        }
    },

    // Public API
    login(token) {
        sessionState.token = token;
        setTokenStorages(token);
        updateUI(true);
    },

    logout() {
        sessionState.token = null;
        resetTokenStorages();
        updateUI(false);
    },

    // Helper to check status in JS files
    isLoggedIn() {
        return !!sessionState.token;
    },

    getToken() {
        return sessionState.token;
    },

    // Re-fetches the main nav menu so conditional entries (e.g. an active
    // challenge) can appear/disappear without requiring a full page reload.
    refreshMenu: fetchMenu
};

export { sessionState };